import React, { useState } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import { useDataProvider, useNotify } from "react-admin";
import { FiEye, FiDownload } from "react-icons/fi";
import { useSubmittedVrReportQuickLook } from "../../utils/submittedVrReport";
import { useDownloadVrReport } from "../../utils/downloadVrReport";
import {
  useVrReportContentMode,
  VR_REPORT_CONTENT_MODE_OPTIONS,
} from "../../utils/vrReportContentMode";

type VrReportOptionsMenuProps = {
  // Undefined until a row's button has set the shared anchor (per-row
  // menus share one instance -- see GetsubscriberinboxListDetails.tsx /
  // GetlandloardinboxListDetails.tsx); harmless since anchorEl is also
  // null in that state, so the menu never actually opens without a trnId.
  trnId: string | number | undefined;
  anchorEl: HTMLElement | null;
  onClose: () => void;
};

// ADO 4855: the shared "Generate Report Options" (results / with document
// portfolio / portfolio only, remembered per-browser) + "Report Options"
// (preview vs. download) menu, anchored off each screen's existing
// "View/Print Result" button -- DocumentAnalysis, the subscriber inbox, and
// the landlord inbox all render this the same way.
export default function VrReportOptionsMenu({
  trnId,
  anchorEl,
  onClose,
}: VrReportOptionsMenuProps) {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const openSubmittedVrReport = useSubmittedVrReportQuickLook(dataProvider);
  const downloadVrReport = useDownloadVrReport(dataProvider);
  const [contentMode, setContentMode] = useVrReportContentMode();
  const [busyAction, setBusyAction] = useState<"preview" | "download" | null>(
    null
  );

  const runAction = async (
    actionName: "preview" | "download",
    action: () => Promise<unknown>,
    failureMessage: string
  ) => {
    if (busyAction || trnId === undefined) return;
    setBusyAction(actionName);
    try {
      await action();
      onClose();
    } catch (error: any) {
      notify(error?.message || failureMessage, {
        type: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
      {VR_REPORT_CONTENT_MODE_OPTIONS.map((option) => (
        <MenuItem
          key={option.value}
          selected={contentMode === option.value}
          onClick={() => setContentMode(option.value)}
        >
          <ListItemIcon>
            {contentMode === option.value ? (
              <CheckIcon fontSize="small" />
            ) : null}
          </ListItemIcon>
          <ListItemText>{option.label}</ListItemText>
        </MenuItem>
      ))}
      <Divider />
      <MenuItem
        disabled={Boolean(busyAction) || trnId === undefined}
        onClick={() =>
          trnId !== undefined &&
          runAction(
            "preview",
            () => openSubmittedVrReport(trnId, contentMode),
            "Failed to open the submitted report"
          )
        }
      >
        <ListItemIcon>
          {busyAction === "preview" ? (
            <CircularProgress size={16} />
          ) : (
            <FiEye size={16} />
          )}
        </ListItemIcon>
        <ListItemText>Preview</ListItemText>
      </MenuItem>
      <MenuItem
        disabled={Boolean(busyAction) || trnId === undefined}
        onClick={() =>
          trnId !== undefined &&
          runAction(
            "download",
            () => downloadVrReport(trnId, contentMode),
            "Failed to download the report"
          )
        }
      >
        <ListItemIcon>
          {busyAction === "download" ? (
            <CircularProgress size={16} />
          ) : (
            <FiDownload size={16} />
          )}
        </ListItemIcon>
        <ListItemText>Download</ListItemText>
      </MenuItem>
    </Menu>
  );
}
